import { useEffect, useMemo, useState } from 'react';
import { isEmpty } from 'lodash';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  CommonButton,
  EmptyStateView,
  ListSkeleton,
  PullRefreshContainer,
  SpinnerLoader,
  StatusBadge,
} from '../../components';
import { getOrderStatusStyle, getOrderStatusText } from '../../helper';
import {
  isOrderLineOutOfStock,
  orderHasOutOfStockLine,
} from '../../helper/is-order-line-out-of-stock';
import { OrderInfoCard } from './order-info-card';
import {
  OrderDiscountLevel,
  OrderProductUpdateType,
  OrderStatusEnum,
  SELLER_WEBAPP_ROUTES,
} from '../../constants';
import { useOrderDetailsApi, useOrderDetailsInfo } from '../hooks';
import { PNGS, SVGS } from '../../assets';
import { OrderLineCard } from '../order-line-card';
import { TouchableOpacity } from '../../common';
import './order-details-screen-content.scss';
import { OrderDetailsModalGroup } from '../order-details-modal-group/order-details-modal-group';
import { getFormattedAmount } from '@zonofi/common/functions/format-amount';
import { ReactComponent as ArrowRight } from '@zonofi/svg-icons/arrow_right.svg';
import { ReactComponent as InfoIcon } from '@zonofi/svg-icons/input-info.svg';
import { ReactComponent as CheckIcon } from '@zonofi/svg-icons/check-bold.svg';
import { ReactComponent as CloseIcon } from '@zonofi/svg-icons/close-bold.svg';

import {
  useAppSelector,
  getAppType,
  getCatalogConfig,
  getOrderStatus,
  getOrderPricelist,
  dispatch,
  updateOrderPricelist,
  OrderDataLine,
  OrderDataSummary,
  OrderLine,
  UOM,
  APP_TYPE,
  updateSearchKey,
  PoOrderShipping,
  updateExtraDetails,
  OrderDetailsResponse,
} from '@zonofi/zono-money-store';
import {
  AdvancePaymentActionCard,
  CartOtherDetails,
  PromotionsModal,
  useAdvancePaymentActions,
} from '../../cart';

const BOTTOM_ACTION_STATUS: Record<string, OrderStatusEnum[]> = {
  [APP_TYPE.SELLER_HUB]: [
    OrderStatusEnum.ADDING_ITEMS,
    OrderStatusEnum.SUBMITTED_BY_CUSTOMER,
    OrderStatusEnum.APPROVED,
  ],
  [APP_TYPE.FIELDS_OPS]: [
    OrderStatusEnum.ADDING_ITEMS,
    OrderStatusEnum.SUBMITTED_BY_CUSTOMER,
  ],
};

interface OrderDetailsScreenContentProps {
  selectedTab?: number;
  setOrderData?: (state: OrderDetailsResponse) => void;
  setSelectedTab?: React.Dispatch<React.SetStateAction<number>>;
}

export const OrderDetailsScreenContent: React.FC<
  OrderDetailsScreenContentProps
> = ({ selectedTab = 0, setOrderData = () => {}, setSelectedTab }) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const orderNumId = searchParams.get('orderId');
  const orderStatus = useAppSelector(getOrderStatus);

  const appType = useAppSelector(getAppType);
  const { showOutOfStockTag } = useAppSelector(getCatalogConfig);
  const [showDiscountModal, setShowDiscountModal] = useState(false);
  const selectedPricelist = useAppSelector(getOrderPricelist);

  const [openReplaceProductDrawer, setOpenReplaceProductDrawer] =
    useState<boolean>(false);
  const [openPricelistDrawer, setOpenPriceListDrawer] =
    useState<boolean>(false);

  const onSelectPricelist = (pricelistId: string, pricelistName: string) => {
    dispatch(updateOrderPricelist({ pricelistId, pricelistName }));
  };

  const {
    orderDetailsData,
    handleUpdate,
    onRemovePress,
    onCancelOrder,
    onAcceptOrder,
    onLineDiscountRemove,
    cancelOrderRes,
    acceptOrderApiResponse,
    checkoutApiResponse,
    onApplyDiscount,
    onReplacePress,
    onPlaceOrder,
    isLoading,
    onNotifyBuyer,
    notifyBuyerApiResponse,
    triggerRefetch,
    onSaveDetails,
    savingDetailsLoader,
    removeId,
    selectedBenefit,
    showPromotionsModal,
    applyPromotionOnOrder,
    promotionSchemes,
    onSkipPromotion,
    onCancelPromotion,
    openPromotionsModal,
    handleRemove,
    onRequestPayment,
    isRequestingPayment,
    showRequestPayment,
    advanceRemoveLoading,
  } = useOrderDetailsApi({ setOpenReplaceProductDrawer });

  const { manageAdvanceActions } = useAdvancePaymentActions({
    setSelectedTab,
    handleRemove,
    removeLoading: advanceRemoveLoading,
  });

  const [showLocationModal, setShowLocationModal] = useState<boolean>(false);
  const [selectedLocation, setSelectedLocation] = useState<PoOrderShipping>({
    ...(orderDetailsData?.shippingAddress as PoOrderShipping),
  });

  const canEditLine = useMemo(() => {
    return (
      orderStatus === OrderStatusEnum.ADDING_ITEMS ||
      orderStatus === OrderStatusEnum.SUBMITTED_BY_CUSTOMER
    );
  }, [orderStatus]);

  useEffect(() => {
    if (orderDetailsData?.priceListId) {
      const { priceListId } = orderDetailsData;
      dispatch(
        updateOrderPricelist({
          pricelistId: priceListId,
          pricelistName: '',
        })
      );
    }
  }, [orderDetailsData?.priceListId]);

  const { orderRelatedInfo } = useOrderDetailsInfo(
    orderDetailsData as OrderDetailsResponse
  );

  const [orderDetailsInfo, setOrderDetailsInfo] = useState<{
    inputValues: Record<string, string>;
    includePaymentLink: boolean;
  }>();
  const [showOrderSummary, setShowOrderSummary] = useState(false);
  const [lineDiscountData, setLineDiscountData] = useState<OrderDataLine>();
  const [orderLineDiscountData, setOrderLineDiscountData] =
    useState<OrderDataSummary>();
  const [orderDiscountType, setOrderDiscountType] =
    useState<OrderDiscountLevel>();
  const [uomModalState, setUomModalState] = useState<{
    open: boolean;
    line?: OrderLine;
  }>({
    open: false,
    line: undefined,
  });
  const [openCheckoutSuccess, setOpenCheckoutSuccess] = useState<{
    open: boolean;
    message: string;
  }>({
    open: false,
    message: '',
  });
  const [showCancelModal, setShowCancelModal] = useState(false);

  const hasAvailablePromotions = promotionSchemes.length > 0;

  useEffect(() => {
    if (notifyBuyerApiResponse.isSuccess) {
      setOpenCheckoutSuccess({
        open: true,
        message: 'Buyer has been notified successfully',
      });
    }
  }, [notifyBuyerApiResponse]);

  const openUomModal = (line: OrderLine) => {
    setUomModalState({ open: true, line });
  };

  const closeUomModal = () => {
    setUomModalState({ open: false, line: undefined });
  };

  const onChangeUom = (uom: UOM) => {
    const lineItem = uomModalState.line;
    if (lineItem) {
      handleUpdate(
        {
          ...lineItem,
          newQnty: Number(lineItem.quantity) * uom.packSize,
        },
        OrderProductUpdateType.QUANTITY
      );
    }
    closeUomModal();
  };

  const onCloseReplace = () => {
    triggerRefetch();
    dispatch(updateSearchKey(''));
    setOpenReplaceProductDrawer(false);
  };

  const getOrderSummaryData = (): OrderDataSummary | undefined => {
    if (!orderDetailsData) return;

    const validLines = orderDetailsData.lines.filter(
      (line) =>
        typeof line?.productVariant?.id === 'number' &&
        !isNaN(line?.productVariant?.id)
    );

    return {
      total: Number(orderDetailsData.total),
      tax: Number(orderDetailsData.taxAmount),
      totalWithTax: Number(orderDetailsData.totalWithTax),
      discount: Number(orderDetailsData.discount),
      shippingLines: orderDetailsData.shippingLines ?? [],
      lines: validLines.map(
        (line): OrderDataLine => ({
          price: Number(line.unitPrice),
          newQty: Number(line.newQnty),
          quantity: Number(line.quantity),
          packSize: line.packSize,
          productVariantId: Number(line.productVariantId),
        })
      ),
    };
  };

  const onAddOrderLevelDiscount = () => {
    const summary = getOrderSummaryData();
    if (summary) setOrderLineDiscountData(summary);

    setOrderDiscountType('order');
    setShowOrderSummary(false);
    setShowDiscountModal(true);
  };

  useEffect(() => {
    if (!isEmpty(orderDetailsData)) {
      setOrderData(orderDetailsData);
    }
  }, [orderDetailsData]);

  const orderDiscount = orderDetailsData?.orderDiscount;

  const hasOutOfStockItem = useMemo(
    () =>
      orderStatus === OrderStatusEnum.ADDING_ITEMS &&
      orderHasOutOfStockLine(orderDetailsData?.lines, !!showOutOfStockTag),
    [orderDetailsData?.lines, showOutOfStockTag]
  );

  const isBottomActionAllowed = useMemo(() => {
    return (
      BOTTOM_ACTION_STATUS[appType]?.includes(orderStatus as OrderStatusEnum) ??
      false
    );
  }, [appType, orderStatus]);

  const fetchLocation = () => {
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        const { latitude, longitude } = pos.coords;
        setSelectedLocation((prev) => ({ ...prev, latitude, longitude }));
      },
      (err) => {
        console.log('Location Error: ', err);
      },
      { enableHighAccuracy: false }
    );
  };

  const advancePaymentCardProps = useMemo(() => {
    const isValidStatus =
      orderStatus === OrderStatusEnum.ADDING_ITEMS ||
      orderStatus === OrderStatusEnum.SUBMITTED_BY_CUSTOMER;
    if (!isValidStatus) return null;

    // If advancePayment exists on order
    if (Number(orderDetailsData?.advancePayment)) {
      return {
        advancePayment: Number(orderDetailsData?.advancePayment) || 0,
        advancePaymentType: orderDetailsData?.advancePaymentType as
          | 'amount'
          | 'percentage',
        advancePaymentAmount:
          Number(orderDetailsData?.advancePaymentAmount) || 0,
      };
    }
    return null;
  }, [orderStatus, orderDetailsData]);

  return (
    <>
      <PullRefreshContainer onRefresh={triggerRefetch}>
        {selectedTab === 0 && (
          <div className="order-details-container">
            <div className="order-details-summary-card">
              {orderDetailsData?.status && (
                <StatusBadge
                  status={getOrderStatusText(orderDetailsData?.status ?? '')}
                  style={getOrderStatusStyle(orderDetailsData?.status ?? '')}
                />
              )}
              <span className="order-details-summary-card-heading font-semibold">
                {`Order from ${orderDetailsData?.distributorFirmName ?? 'N/A'}`}
              </span>

              {orderRelatedInfo?.map((item) => (
                <OrderInfoCard
                  key={item.id}
                  icon={item.icon}
                  title={item.title}
                  value={item.value}
                />
              ))}
              {/* <div className="order-details-summary-card-priceList font-semibold">
                <img src={SVGS.PriceListIcon} width={20} height={20} />
                Price List:{' '}
                {selectedPricelist?.pricelistName && (
                  <span className="font-semibold selected">
                    {selectedPricelist?.pricelistName}
                  </span>
                )}
                {canEditLine && (
                  <span
                    onClick={() => {
                      setOpenPriceListDrawer?.(true);
                    }}
                    className="font-semibold"
                  >
                    {selectedPricelist?.pricelistName ? 'Replace' : 'Select'}
                  </span>
                )}
              </div> */}
            </div>

            <div className="order-details-product-card">
              {isLoading ? (
                <ListSkeleton />
              ) : orderDetailsData?.lines?.length ? (
                <>
                  <span className="order-details-product-card-title font-semibold">
                    {`${orderDetailsData?.lines.length < 10 ? `0${orderDetailsData?.lines.length}` : `${orderDetailsData?.lines.length}`} Products`}
                  </span>

                  <div className="order-details-products-wrapper">
                    {orderDetailsData?.lines.map((lineItem, index) => {
                      return (
                        <OrderLineCard
                          key={`${lineItem.productVariant?.id}-${index}`}
                          lineItem={lineItem}
                          isOutOfStock={isOrderLineOutOfStock(
                            lineItem,
                            !!showOutOfStockTag
                          )}
                          onRemovePress={onRemovePress}
                          handleUpdate={handleUpdate}
                          onLineDiscountRemove={onLineDiscountRemove}
                          showRemoveLine={
                            orderDetailsData?.status ===
                              OrderStatusEnum.ADDING_ITEMS ||
                            orderDetailsData?.lines?.length > 1
                          }
                          onAddingLineDiscount={(data, discountType) => {
                            setLineDiscountData(data);
                            setShowDiscountModal(true);
                            setShowOrderSummary(false);
                            setOrderDiscountType(discountType);
                          }}
                          onReplacePress={onReplacePress}
                          openUomModal={openUomModal}
                          isRemoving={removeId === lineItem?.id}
                        />
                      );
                    })}
                    {hasAvailablePromotions && (
                      <div className="order-details-footer-benefit-text font-semibold">
                        Benefits
                      </div>
                    )}

                    {!selectedBenefit && hasAvailablePromotions && (
                      <div
                        className="order-details-footer-apply-benefits font-semibold"
                        onClick={openPromotionsModal}
                      >
                        <img src={PNGS.SchemePercentIcon} alt="" />
                        <span>Apply Benefits</span>
                        <ArrowRight color="#0067FF" />
                      </div>
                    )}
                    {selectedBenefit && (
                      <div className="order-details-selected-benefit">
                        <div className="order-details-selected-benefit-content">
                          <img src={PNGS.ManualOfferIcon} alt="benefit" />
                          <div className="order-details-selected-benefit-content-text">
                            <span className="font-semibold">
                              {selectedBenefit?.name}
                            </span>
                            {selectedBenefit.description ? (
                              <span className="font-regular">
                                {selectedBenefit?.description}
                              </span>
                            ) : null}
                            <span className="order-details-selected-benefit-meta font-regular">
                              {selectedBenefit?.code}
                              {selectedBenefit?.code &&
                                selectedBenefit?.minOrderQuantity &&
                                ` • `}
                              {selectedBenefit?.minOrderQuantity &&
                                `Min Order Quantity: ${selectedBenefit?.minOrderQuantity}`}
                            </span>
                            <button
                              className="order-details-selected-benefit-remove font-semibold"
                              onClick={openPromotionsModal}
                            >
                              Change benefit
                            </button>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </>
              ) : (
                <EmptyStateView
                  imageSrc={PNGS.NoOrdersFound}
                  message="No Products found"
                />
              )}
            </div>
          </div>
        )}
        {selectedTab === 1 && (
          <CartOtherDetails
            onLocationClick={() => {
              fetchLocation();
              setShowLocationModal(true);
            }}
            location={selectedLocation}
            setOrderDetailsInfo={setOrderDetailsInfo}
            isEditable={
              orderStatus === OrderStatusEnum.ADDING_ITEMS ||
              orderStatus === OrderStatusEnum.SUBMITTED_BY_CUSTOMER
            }
          />
        )}
      </PullRefreshContainer>
      {selectedTab === 0 &&
        !isEmpty(orderDetailsData) &&
        isBottomActionAllowed && (
          <div className="order-details-bottom-container">
            {advancePaymentCardProps && (
              <AdvancePaymentActionCard
                advancePayment={advancePaymentCardProps.advancePayment}
                advancePaymentType={advancePaymentCardProps.advancePaymentType}
                advancePaymentAmount={
                  advancePaymentCardProps.advancePaymentAmount
                }
                actions={manageAdvanceActions}
              />
            )}
            {orderStatus === OrderStatusEnum.ADDING_ITEMS &&
              hasOutOfStockItem && (
                <div className="order-details-bottom-container-oos-warning">
                  <InfoIcon color="#fff" />
                  <span className="font-semibold">
                    Your cart contains an out-of-stock item. Please replace or
                    remove it to continue.
                  </span>
                </div>
              )}
            <div className="order-details-bottom-container-view">
              {orderDiscount?.discountType && (
                <>
                  <span className="order-details-bottom-container-discount font-medium">
                    <img src={SVGS.DiscountIcon} />
                    {orderDiscount.discountType === 'percent'
                      ? `${orderDiscount.discount}% Discount Applied of ${getFormattedAmount(Number(orderDetailsData.discount))}`
                      : `${getFormattedAmount(orderDiscount.discount)} Discount Applied`}
                  </span>
                  <div className="order-details-bottom-container-border" />
                </>
              )}
              {canEditLine && (
                <TouchableOpacity onPress={onAddOrderLevelDiscount}>
                  <span className="order-details-bottom-container-text font-medium">
                    {orderDiscount?.discountType
                      ? 'Edit Discount'
                      : '+ Add Discount'}
                  </span>
                </TouchableOpacity>
              )}
            </div>
            <div
              className={`order-details-button-view ${orderStatus?.toLowerCase()}`}
            >
              {orderStatus === OrderStatusEnum.SUBMITTED_BY_CUSTOMER && (
                <>
                  {showRequestPayment ? (
                    <TouchableOpacity
                      onPress={() => setShowCancelModal(true)}
                      className="cancel-button-view"
                      disabled={cancelOrderRes.isLoading}
                    >
                      {cancelOrderRes.isLoading ? (
                        <SpinnerLoader
                          color="#fff"
                          size={20}
                          className="cancel-button-view-spinner"
                        />
                      ) : (
                        <CloseIcon color="#fff" />
                      )}
                    </TouchableOpacity>
                  ) : (
                    <CommonButton
                      text="Cancel Order"
                      buttonStyle="cancel-button-view-v2"
                      onPress={onCancelOrder}
                      spinnerColor={'white'}
                      startIcon={<CloseIcon color="#fff" />}
                      spinnerStyle="cancel-button-view-spinner"
                      loading={cancelOrderRes.isLoading}
                      spinnerSize={20}
                    />
                  )}

                  <CommonButton
                    text="Accept Order"
                    buttonStyle={`accept-button-view ${
                      hasOutOfStockItem ? 'disabled' : ''
                    }`}
                    disabled={hasOutOfStockItem}
                    onPress={onAcceptOrder}
                    startIcon={<CheckIcon color="#fff" />}
                    spinnerStyle="accept-button-view-spinner"
                    spinnerColor={'white'}
                    spinnerSize={20}
                    loading={acceptOrderApiResponse.isLoading}
                  />
                  {showRequestPayment && (
                    <CommonButton
                      text="Request Payment"
                      buttonStyle="request-button-view"
                      onPress={onRequestPayment}
                      spinnerColor={'white'}
                      spinnerStyle="request-button-view-spinner"
                      loading={isRequestingPayment}
                      spinnerSize={20}
                    />
                  )}
                </>
              )}

              {orderStatus === OrderStatusEnum.ADDING_ITEMS && (
                <>
                  <CommonButton
                    text="Ask Confirmation"
                    onPress={onNotifyBuyer}
                    buttonStyle={`confrim-button-view ${
                      hasOutOfStockItem ? 'disabled' : ''
                    }`}
                    disabled={hasOutOfStockItem}
                    spinnerColor={'white'}
                    endIcon={<ArrowRight color="#ffffff" />}
                    spinnerStyle="accept-button-view-spinner"
                    loading={notifyBuyerApiResponse.isLoading}
                    spinnerSize={20}
                  />
                  <CommonButton
                    text="Checkout"
                    buttonStyle={`accept-button-view ${
                      hasOutOfStockItem ? 'disabled' : ''
                    }`}
                    disabled={hasOutOfStockItem}
                    onPress={onPlaceOrder}
                    endIcon={<ArrowRight color="#ffffff" />}
                    spinnerStyle="accept-button-view-spinner"
                    spinnerColor={'white'}
                    spinnerSize={20}
                    loading={checkoutApiResponse.isLoading}
                  />
                </>
              )}
              {appType === APP_TYPE.SELLER_HUB &&
                orderStatus === OrderStatusEnum.APPROVED && (
                  <CommonButton
                    text="Upload Invoice"
                    buttonStyle="upload-button-view"
                    onPress={() => {
                      dispatch(
                        updateExtraDetails({
                          customerName:
                            orderDetailsData?.distributorFirmName ?? 'N/A',
                          orderRefCode: orderDetailsData?.refOrderNumber ?? '',
                          customerId: orderDetailsData?.customerId ?? '',
                          orderNumId: orderNumId ?? '',
                          orderId: orderDetailsData?.poFile?.id ?? '',
                        })
                      );
                      navigate(
                        `${SELLER_WEBAPP_ROUTES.READY_TO_DISPATCH}/${orderDetailsData?.customerId}`
                      );
                    }}
                  />
                )}
            </div>
          </div>
        )}
      {selectedTab === 1 && (
        <div className="other-details-save-view">
          <CommonButton
            text="Save Details"
            onPress={() => {
              onSaveDetails?.(
                orderDetailsInfo?.inputValues ?? {},
                orderDetailsInfo?.includePaymentLink ?? false
              );
            }}
            buttonStyle="other-details-save-view-button"
            loading={savingDetailsLoader}
            spinnerColor={'white'}
            spinnerSize={20}
            disabled={
              orderStatus !== OrderStatusEnum.ADDING_ITEMS &&
              orderStatus !== OrderStatusEnum.SUBMITTED_BY_CUSTOMER
            }
          />
        </div>
      )}
      <OrderDetailsModalGroup
        discount={{
          open: showDiscountModal,
          onClose: () => setShowDiscountModal(false),
          onApply: onApplyDiscount,
          data: lineDiscountData as OrderDataLine,
          orderDiscountType: orderDiscountType as OrderDiscountLevel,
          orderSummary: orderLineDiscountData,
          orderLevelDiscount: orderDetailsData?.orderDiscount,
          isOrderSummary: showOrderSummary,
        }}
        replaceProduct={{
          open: openReplaceProductDrawer,
          onClose: onCloseReplace,
        }}
        pricelist={{
          open: openPricelistDrawer,
          onClose: () => setOpenPriceListDrawer(false),
          onSelectPricelist: onSelectPricelist,
        }}
        uom={{
          open: uomModalState.open,
          onClose: closeUomModal,
          onUomChange: onChangeUom,
          currentLine: uomModalState.line,
        }}
        success={{
          ...openCheckoutSuccess,
          onClose: () => {
            setOpenCheckoutSuccess({
              open: false,
              message: '',
            });
          },
          showShareButton: false,
          mainText: 'Done',
          onMainTextPress: () => {
            setOpenCheckoutSuccess({
              open: false,
              message: '',
            });
          },
        }}
        location={{
          open: showLocationModal,
          onClose: () => setShowLocationModal(false),
          posCords: {
            latitude: selectedLocation.latitude || 0,
            longitude: selectedLocation.longitude || 0,
          },
          onLocationSelected: (location) =>
            setSelectedLocation((prev) => ({ ...prev, streetLine1: location })),
        }}
        cancel={{
          open: showCancelModal,
          onClose: () => setShowCancelModal(false),
          onCancelPress: onCancelOrder,
          isLoading: cancelOrderRes.isLoading,
        }}
      />
      {showPromotionsModal && (
        <PromotionsModal
          schemes={promotionSchemes}
          onApply={applyPromotionOnOrder}
          onSkip={() => {
            onSkipPromotion();
          }}
          selectedBenefit={selectedBenefit}
          onCancel={onCancelPromotion}
          isApplying={false}
          modalTitle={
            selectedBenefit ? 'Change Benefit' : 'Cash Discount Benefit'
          }
        />
      )}
    </>
  );
};
